import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

// Cortex Breadcrumb — navigable trail with ChevronRight separators. The final item is the
// current page (aria-current="page"); items with a `to` render as react-router Links.

export interface BreadcrumbItem {
  label: string;
  to?: string | undefined;
}

export interface BreadcrumbProps {
  items: BreadcrumbItem[];
  className?: string | undefined;
}

export function Breadcrumb({ items, className }: BreadcrumbProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const linkColor = isDark
    ? 'text-white/50 hover:text-white'
    : 'text-slate-500 hover:text-slate-900';
  const currentColor = isDark ? 'text-white' : 'text-slate-900';
  const sepColor = isDark ? 'text-white/20' : 'text-slate-300';

  return (
    <nav aria-label="Breadcrumb" className={className}>
      <ol className="flex flex-wrap items-center gap-1.5 text-sm">
        {items.map((item, i) => {
          const isLast = i === items.length - 1;
          return (
            <Fragment key={`${item.label}-${i}`}>
              <li className="flex items-center">
                {isLast || !item.to ? (
                  <span
                    aria-current={isLast ? 'page' : undefined}
                    className={`font-medium ${isLast ? currentColor : linkColor}`}
                  >
                    {item.label}
                  </span>
                ) : (
                  <Link
                    to={item.to}
                    className={`font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 ${linkColor}`}
                  >
                    {item.label}
                  </Link>
                )}
              </li>
              {!isLast && (
                <li aria-hidden="true" className="flex items-center">
                  <ChevronRight size={14} className={sepColor} />
                </li>
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
